import Navigation from "../Navigation";
import UserLoginBox from "../UserLoginBox";
import { IBlockchainViewProps } from "./Blockchain.interfaces";
import BookDemo from "../BookDemo";
import { SectionBlock, SectionBlockProps } from "../SectionBlock/SectionBlock";
import { Link } from "react-router-dom";
// import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
// import data from "../ResponisveCarousel.json";
import Message from "../Message";
import FeatureCard, { FeatureCardProps } from "../FeaturesCard";
import React, { useState, useRef, useEffect } from "react";
const countryCodes: Array<{ name: string; dial_code: string; code: string }> = require("../CountryCodes.json");


const services = [
    'Contract architecture design',
    'Gas fee optimization & performance tuning',
    'AI-powered automated security audits',
    'Cross-chain compatibility integration',
];

// Reusable list item component
const ServiceListItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <li className="flex items-center space-x-3 font-[500] text-white">
        <img
            src="/images/blockchain/staricon.svg"
            alt="Star Illustration"
            className="max-w-full h-auto"
        />
        <span>{children}</span>
    </li>
);

const UseCaseItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <li className="flex items-center space-x-3 text-white  text-[18px] font-[500]">
        <img
            src="/images/blockchain/staricon.svg"
            alt="Star Illustration"
            className="max-w-full h-auto"
        />
        <span>{children}</span>
    </li>
);

// Reusable list item for the "Benefits" section
const BenefitItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <li className="flex items-center space-x-3 text-lg">
        {/* <StarIcon /> */}
        <img
            src="/images/blockchain/staricon.svg"
            alt="Star Illustration"
            className="max-w-full h-auto"
        />
        <span>{children}</span>
    </li>
);

// Reusable list item for the "Why Invincible Ocean?" section
const CheckListItem: React.FC<{ children: React.ReactNode; isHighlighted?: boolean }> = ({ children, isHighlighted }) => (
    <div className={`flex items-center space-x-4 p-4 bg-white rounded-lg shadow-md transition-all duration-300 border-2 ${isHighlighted ? 'border-blue-500  bg-[#6091E23B] animated-border scale-[1.08]' : 'border-transparent'} hover:border-2 hover:border-blue-500 hover:shadow-blue-200`}>
        <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
            </svg>
        </div>
        <span className="text-[#1C2347] font-medium">{children}</span>
    </div>
);


// --- Data for the section ---
const tokenizationSolutions = [
    { title: 'Asset Tokenization', description: 'Real estate, commodities, intellectual property, and digital assets tokenized for fractional ownership.' },
    { title: 'AI-Priced Assets', description: 'Machine learning models to dynamically adjust token valuations based on market sentiment and historical trends.' },
    { title: 'Smart Rewards & Loyalty', description: 'AI-powered reward algorithms for token-gated communities.' },
    { title: 'Compliance-First Tokenization', description: 'Built-in KYC/AML checks within token transactions.' },
];

const benefits = [
    'Unlock liquidity for traditionally illiquid assets',
    'Transparent, auditable, and automated governance',
    'Intelligent token economics driven by AI insights',
];

const whyUsPoints = [
    'Proven expertise with 5+ major banks and 200+ enterprise clients',
    'Full-stack blockchain development capabilities',
    'AI & Metaverse integration to enhance blockchain adoption',
    'Compliance-ready architecture for global markets',
    '150+ country deployment experience',
];


export default function BlockchainView(props: IBlockchainViewProps) {
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => {
            setHighlightedIndex((prev) => (prev + 1) % whyUsPoints.length);
        }, 1000); // 1 seconds per item
        return () => clearInterval(interval);
    }, []);
    const sectionData: SectionBlockProps[] = [
        {
            steps: [
                {
                    number: '01',
                    title: 'Ideation & Consulting',
                    description: 'Understanding your vision, defining blockchain use cases, and choosing the right architecture.',
                },
                {
                    number: '02',
                    title: 'Architecture & Protocol Selection',
                    description: 'Deciding between public, private, or hybrid blockchain, and selecting the right tech stack.',
                },
            ],
            imageUrl: '/images/blockchain/ideation.webp', // <-- Replace with your image
            imageSide: 'right',
            showButton: true,
            demoController: props.demoController,
        },
        {
            steps: [
                {
                    number: '03',
                    title: 'Smart Contract & App Development',
                    description: 'Crafting secure, efficient, and gas-optimized contracts using Solidity, Rust, or Move.',
                },
                {
                    number: '04',
                    title: 'Maintenance & Upgrades',
                    description: 'Continuous monitoring, auditing, and feature enhancements to keep your product competitive.',
                },
            ],
            imageUrl: '/images/blockchain/appdev.webp', // <-- Replace with your image
            imageSide: 'left',
            showButton: true,
            demoController: props.demoController,
        },
        {
            steps: [
                {
                    number: '05',
                    title: 'Deployment & Scalability',
                    description: 'Launching on preferred networks with multi-chain and cross-chain capabilities.',
                },
                {
                    number: '06',
                    title: 'Integration & AI Augmentation',
                    description: 'Enhancing blockchain workflows with AI-powered analytics, fraud detection, and automation.',
                },
            ],
            imageUrl: '/images/blockchain/aiblock.webp', // <-- Replace with your image
            imageSide: 'right',
            showButton: true,
            demoController: props.demoController,
        },
    ];
    const featureCardsData: FeatureCardProps[] = [
        {
            icon: "/images/blockchain/solidity.svg",
            title: 'Solidity',
            description: 'The gold standard for Ethereum, Polygon, and EVM-compatible chains.',
        },
        {
            icon: "/images/blockchain/rust.svg",
            title: 'Rust',
            description: 'High-performance smart contracts for Solana, NEAR, and Polkadot ecosystems.',
        },
        {
            icon: "/images/blockchain/move.svg",
            title: 'Move',
            description: 'Secure and flexible contracts for Aptos and Sui blockchains.',
        },
    ];

    return (
        <>
            {props.showMessage && (
                <Message
                    messageText={props.messageText}
                    messageClass={props.messageClass}
                    demoController={props.messageController}
                />
            )}
            {props.showlogin === true && !props.user && (
                <UserLoginBox
                    redirectionURL="/metaverse/65b741def8309762720ad53f"
                    loginController={props.loginController}
                /> // Since the line 122, says Login to Eternity City, but can be changed later => TP | 2024-01-30 14:39:44
            )}
            {props.showDemo === true && (
                <BookDemo demoController={props.demoController} />
            )}
            <div className="bg-cover   font-['roboto'] relative h-auto">
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute  top-0 left-0 w-full h-full object-cover"
                >
                    <source
                        src="./images/blockchain/blockchainheader.webm"
                        type="video/webm"
                    />
                    Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 bg-black opacity-[.35]"></div>
                <Navigation scrollToBlockchainSection={props.scrollToBlockchainSection} />
                <div className="max-w-screen-2xl font-['Roboto'] flex max-md:flex-col  relative z-10  px-2 mx-auto max-md:mx-2 py-48 max-md:py-24 font-roboto font-semibold text-white">
                    <div className="w-1/2 max-md:w-full" >
                        <div className="text-left">
                            <h2 className="text-[64px] max-lg:text-[48px] leading-[100%] max-w-[800px] max-md:text-[45px] max-md:leading-[50px] max-md:py-5">
                                From concept to deployment, Invincible Ocean delivers end-to-end blockchain product lifecycle management
                            </h2>
                        </div>
                        <div className="text-left my-10 max-md:mt-2 text-[16px]">
                            <p>
                                Combining deep blockchain expertise with AI-driven intelligence and tokenization strategies. Whether it’s Solidity, Rust, or Move-based smart contracts, or public, private, and hybrid blockchain solutions, we build products that are secure, scalable, and ready for mass adoption.
                            </p>
                        </div>
                        <div className="text-left flex gap-2 max-md:flex-col pt-16 max-md:pt-5">
                            <button
                                className="bg-blue-100 px-10 max-md:px-[78px]    py-3 text-white  transition duration-300 ease-in-out  hover:scale-[1.05] hover:bg-gradient-to-l rounded-md mr-4 max-md:mr-0 bg-gradient-to-r from-blue-500 to-purple-600"
                                onClick={props.demoController}
                            >
                                Build with Us
                            </button>
                            {/* <Link
                                to="https://dashboard.invincibleocean.com/dashboard"
                                target="_blank"
                            > */}
                            <button
                                className="border-2 bg-[#FFF8F840] text-white max-md:w-full  transition duration-300 ease-in-out hover:scale-[1.05] hover:bg-[#FFF8F860] px-8 max-md: py-3 rounded-md"
                                onClick={props.demoController}
                            >
                                Book a Consultation
                                <i className="fa fa-long-arrow-right ml-4"></i>
                            </button>
                            {/* </Link> */}
                        </div>
                        {/* <div className="flex flex-col max-md:block  mt-20 max-md:mt-10  text-left pb-5">
                            <div className="flex max-md:py-2 pl-2 max-md:pl-0">
                                <div className="mr-6">
                                    <h5 className="text-5xl max-lg:text-4xl">
                                        20 +
                                    </h5>
                                    <p>Clients</p>
                                </div>
                                <div>
                                    <h5 className="text-5xl max-lg:text-4xl">
                                        350 +
                                    </h5>
                                    <p>Robots Deployed</p>
                                </div>
                            </div>
                            <div className="flex max-md:py-2  mt-28 max-md:mt-0 max-xl:mt-5 max-xl:flex-col max-md:block">
                                <div className="flex items-center">
                                    <img
                                        src="./images/dashboardheader/headerusericon.webp"
                                        alt=""
                                        className="pr-5 max-md:p-0 max-md:py-5 max-md:pr-5"
                                    />
                                    <p className="max-w-[300px]">
                                        Accelerating data and AI value to scale and
                                        transform your Conventional business
                                    </p>
                                </div>
                                <div className="flex  items-center">
                                    <img
                                        src="./images/dashboardheader/headersearchicon.webp"
                                        alt=""
                                        className="pr-5 max-md:p-0 max-md:py-5 max-md:pr-5"
                                    />
                                    <p className=" max-w-[300px]">
                                        Engineering Tomorrow: Shaping the Future
                                        with AI
                                    </p>
                                </div>
                            </div>

                        </div> */}
                    </div>
                    <div className="w-1/2 max-md:w-full flex justify-end max-lg:block items-center max-md:w-full">
                        <div className="border-[1px] p-4 max-md:p-2 ">
                            <div className="border shadow-2xl bg-white">
                                <div className="pt-5">
                                    <h5 className="text-[#1C2347] text-[30px] leading-[30px] text-left px-5 max-md:px-2 ">
                                        Send us your query
                                    </h5>
                                </div>
                                <form className="">
                                    <div className="flex max-md:block">
                                        <div className="w-[500px] max-[1080px]:w-full px-5 max-lg:px-2">
                                            <label className="text-xs font-medium text-[#1C2347] text-[14px] leading-[16px] font-[500]">
                                                Name
                                                <span className="text-xs text-pink">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                className="w-full text-[#1C2347] bg-[#E7E7E7DB] text-sm font-normal border border-grey-500 rounded-md py-3 px-4 max-md:px-2 focus:outline-none"
                                                type="text"
                                                placeholder="Enter name"
                                                onChange={
                                                    props.handleDemoFormChange
                                                }
                                                name="name"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="">
                                        <div className="w-[500px] max-[1080px]:w-full px-5 max-lg:px-2">
                                            <label className="text-xs font-medium text-[#1C2347] text-[14px] leading-[16px] font-[500]">
                                                Phone Number
                                                <span className="text-xs text-pink">
                                                    *
                                                </span>
                                            </label>
                                        </div>
                                        <div className="w-full px-5 max-lg:px-2 flex items-end mt-2">
                                            {/* Country code dropdown button */}
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    className="shrink-0 z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-gray-900 bg-gray-100 border border-gray-300 rounded-s-lg hover:bg-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100"
                                                    onClick={() => setShowCountryDropdown((prev: boolean) => !prev)}
                                                    id="dropdown-phone-button"
                                                >
                                                    <span className="me-2">
                                                        <img
                                                            src={`https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/${(
                                                                countryCodes.find((c) => c.dial_code === props.countryCode)?.code || "IN"
                                                            ).toUpperCase()}.svg`}
                                                            alt={
                                                                countryCodes.find((c) => c.dial_code === props.countryCode)?.code || "IN"
                                                            }
                                                            className="inline w-5 h-5 mr-2"
                                                            style={{ display: 'inline-block', verticalAlign: 'middle' }}
                                                        />
                                                    </span>
                                                    {props.countryCode || "+91"}
                                                    <svg className="w-2.5 h-2.5 ms-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4" /></svg>
                                                </button>
                                                {/* Dropdown menu */}
                                                {showCountryDropdown && (
                                                    <div className="z-10 absolute bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-[300px] max-h-60 overflow-y-auto">
                                                        <ul className="py-2 text-sm text-gray-700" aria-labelledby="dropdown-phone-button">
                                                            {countryCodes.map((country) => (
                                                                <li key={country.code}>
                                                                    <button
                                                                        type="button"
                                                                        className="inline-flex w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                        onClick={() => {
                                                                            // Create a synthetic event to match the expected signature
                                                                            const event = {
                                                                                target: {
                                                                                    name: "country-code",
                                                                                    value: country.dial_code,
                                                                                },
                                                                            } as React.ChangeEvent<HTMLSelectElement>;
                                                                            props.handleDemoFormChange(event);
                                                                            setShowCountryDropdown(false);
                                                                        }}
                                                                    >
                                                                        <span className="inline-flex items-center text-[#1C2347] text-[12px] font-[500]">
                                                                            <img
                                                                                src={`https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/${(country.code || 'IN').toUpperCase()}.svg`}
                                                                                alt={country.code}
                                                                                className="inline w-5 h-5 mr-1"
                                                                                style={{ display: 'inline-block', verticalAlign: 'middle' }}
                                                                            />
                                                                            {country.name} ({country.dial_code})
                                                                        </span>
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Phone number input */}
                                            <div className="flex-1 relative w-full">
                                                <input
                                                    type="text"
                                                    id="phone-input"
                                                    className="block p-2.5 py-[10.5px] w-full z-20 text-sm text-[#1C2347] bg-gray-50 rounded-e-lg border-s-0 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                                    pattern="[0-9]{10}"
                                                    placeholder="1234567890"
                                                    required
                                                    name="phone-number"
                                                    value={props.phoneNumber}
                                                    onChange={props.handleDemoFormChange}
                                                    maxLength={10}
                                                />
                                            </div>
                                        </div>
                                        <div className="w-full px-5 max-lg:px-2">
                                            <label className="text-xs font-medium text-[#1C2347] text-[14px] leading-[16px] font-[500]">
                                                Company Name
                                                <span className="text-xs text-pink">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                className="w-full text-[#1C2347] bg-[#E7E7E7DB] text-sm font-normal border border-grey-500 rounded-md py-3 px-4 max-md:px-2 focus:outline-none"
                                                type="text"
                                                placeholder="Enter Comapany Name"
                                                onChange={
                                                    props.handleDemoFormChange
                                                }
                                                name="company-name"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="">
                                        <div className="px-5 max-lg:px-2">
                                            <label className="text-xs font-medium text-[#1C2347] text-[14px] leading-[16px] font-[500]">
                                                Business Email
                                                <span className="text-xs text-pink">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                className="w-full text-[#1C2347] bg-[#E7E7E7DB] text-sm font-normal border border-grey-500 rounded-md py-3 px-4 max-md:px-2 focus:outline-none"
                                                type="text"
                                                placeholder="Enter Buisness Email"
                                                onChange={
                                                    props.handleDemoFormChange
                                                }
                                                name="business-email"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="px-5 max-lg:px-2">
                                        <label className="text-xs font-medium text-[#1C2347] text-[14px] leading-[16px] font-[500]">
                                            Message
                                            <span className="text-xs text-pink">
                                                *
                                            </span>
                                        </label>
                                        <textarea
                                            className="w-full py-3 px-4 max-md:px-2 text-[#1C2347] border bg-[#E7E7E7DB] border-grey-500 rounded-md h-[100px] max-md:h-[50px] text-sm focus:outline-none"
                                            placeholder="Enter message here"
                                            required
                                            onChange={
                                                props.handleDemoFormChange
                                            }
                                            name="message"
                                        />
                                    </div>
                                    <div className="px-4 w-full">
                                        <button
                                            onClick={
                                                props.handleDemoFormSubmit
                                            }
                                            className="bg-blue-100 w-full font-[700] py-3 my-5 text-white transition duration-300 ease-in-out hover:scale-[1.05] hover:bg-gradient-to-l rounded-md mr-4 bg-gradient-to-r from-blue-500 to-purple-600"
                                        >
                                            Submit request
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Section --------------------------------------------------------------------------------- II*/}

            <div
                id="blockchain-lifecycle"
                className="bg-gray-50 py-16 md:py-24 font-sans overflow-hidden font-['Roboto']"
                style={{
                    backgroundImage: `url('/images/blockchain/section1background.webp')`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                }}
            >
                <div className="max-w-screen-2xl mx-auto px-6">
                    {/* --- Main Header --- */}
                    <div className="text-center max-w-4xl mx-auto mb-16 md:mb-24">
                        <h1 className="text-3xl md:text-5xl font-bold text-[#1C2347] mb-4">
                            Blockchain First - End-to-End Product Lifecycle Management
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600">
                            We don't just write code — we build complete blockchain ecosystems.
                        </p>
                    </div>

                    {/* --- Lifecycle Sections --- */}
                    <div className="space-y-16 md:space-y-10 ">
                        {sectionData.map((section, index) => (
                            <SectionBlock
                                key={index}
                                steps={section.steps}
                                imageUrl={section.imageUrl}
                                imageSide={section.imageSide}
                                showButton={section.showButton}
                                demoController={section.demoController}
                            />
                        ))}
                    </div>
                </div>
            </div>


            {/* Section ----------------------------------------------------------------------------------III*/}
            <div className="font-['Roboto']">
                <section id="smart-contract-development" className="bg-gray-50 py-16 md:py-24">
                    <div className="container mx-auto px-6 pt-20 max-md:pt-10">
                        {/* --- Section Header --- */}
                        <div className="text-center max-w-4xl mx-auto mb-12 md:mb-16">
                            <h2 className="text-[64px] md:text-4xl font-bold text-[#1C2347] mb-3 leading-[100%] max-md:text-[36px]">
                                Smart Contract Development – Solidity, Rust, Move
                            </h2>
                            <p className="text-lg text-[#1C2347] font-[500]">
                                We specialize in multi-language smart contract development to cater to diverse blockchain ecosystems.
                            </p>
                        </div>

                        {/* --- Feature Cards Grid --- */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 md:mb-24">
                            {featureCardsData.map((card) => (
                                <FeatureCard key={card.title} {...card} />
                            ))}
                        </div>

                        {/* --- Services Include Section --- */}

                        <div className="relative bg-slate-800 rounded-2xl p-8 md:p-16 text-white overflow-hidden text-left">
                            {/* Background Image */}
                            <div
                                className="absolute inset-0 bg-cover bg-center"
                                style={{ backgroundImage: "url('/images/blockchain/sectionthreeback.webp')" }} // <-- Replace with your image
                            ></div>

                            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center"
                            // style={{ background: `linear-gradient(90.25deg, #1C2347 46.66%, rgba(255, 255, 255, 0) 121.52%)` }}
                            >
                                {/* Left side: Title and list */}
                                <div className="space-y-6">
                                    <h3 className="text-[30px] font-[600]">Our Smart Contract Services Include:</h3>
                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-gray-300">
                                        {services.map(service => <ServiceListItem key={service}>{service}</ServiceListItem>)}
                                    </ul>
                                    <button
                                        className="bg-blue-100 font-[500] px-10 max-md:px-[78px]    py-3 text-white  transition duration-300 ease-in-out  hover:scale-[1.05] hover:bg-gradient-to-l rounded-md mr-4 max-md:mr-0 bg-gradient-to-r from-blue-500 to-purple-600"
                                        onClick={props.demoController}
                                    >
                                        Book a Demo
                                    </button>
                                </div>
                                {/* Right side: Button */}
                                <div className="flex md:justify-end">

                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* === Blockchain Solutions Section === */}
                <div
                    id="blockchain-solutions"
                    className="relative bg-slate-900 text-white py-20 md:py-32 bg-cover bg-center text-left "
                    style={{ backgroundImage: "url('/images/blockchain/backdropsecthreeparttwo.webp')" }} // <-- Replace with your image
                >
                    {/* <div className="absolute inset-0 bg-slate-900 opacity-50"></div> */}
                    <div className="container mx-auto px-6 relative z-10">

                        {/* --- Top Content Block --- */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-md:gap-4 mb-16 bg-[#86868636] rounded-[20px] backdrop-blur-md  p-10">
                            {/* Left: Main Titles */}
                            <div className="max-w-xl">
                                <h2 className="text-[40px] md:text-[64px] font-bold leading-tight text-white">
                                    Public, Private & Hybrid Blockchain Solutions
                                </h2>
                                <p className="mt-4 text-lg text-white font-[500]">
                                    We help enterprises choose the right blockchain model.
                                </p>
                            </div>
                            {/* Right: Descriptions */}
                            <div className="space-y-6  flex flex-col justify-between rounded-lg text-left">
                                <div>
                                    <h4 className="font-bold text-xl mb-1 text-white">Private Blockchain</h4>
                                    <p className="text-white font-[400]">Permissioned, secure, and controlled environments for enterprise operations like supply chain or banking.</p>
                                </div>
                                <div>
                                    <h4 className="font-bold text-xl mb-1 text-white">Hybrid Blockchain</h4>
                                    <p className="text-white font-[400]">Combining the best of both worlds - public transparency with private control, perfect for compliance-driven industries.</p>
                                </div>
                                <div>
                                    <h4 className="font-bold text-xl mb-1 text-white">Public Blockchain</h4>
                                    <p className="text-white font-[400]">Open, transparent, and decentralized for maximum trust. Ideal for NFTS, DeFi, and community-driven projects.</p>
                                </div>
                            </div>
                        </div>

                        {/* --- Bottom Use Cases Block --- */}
                        <div className="bg-[#86868636] p-8 rounded-[20px] backdrop-blur-md py-16">
                            <h4 className="font-bold text-xl mb-6">Example Use Cases:</h4>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-gray-200">
                                <UseCaseItem>CBDC-ready financial platforms</UseCaseItem>
                                <UseCaseItem>Tokenized supply chain systems</UseCaseItem>
                                <UseCaseItem>Cross-border payments</UseCaseItem>
                                <UseCaseItem>Enterprise-grade DeFi</UseCaseItem>
                            </ul>
                        </div>

                    </div>
                </div>
            </div>

            {/* Section ----------------------------------------------------------------------------------IV*/}
            <div className="font-['Roboto']">
                <div id="ai-tokenization" className="bg-gray-50">
                    <div className="container mx-auto px-6 py-16 md:py-24">

                        {/* === Section Header === */}
                        <div className="text-center max-w-4xl mx-auto">
                            <h1 className="text-3xl md:text-[48px] leading-[100%] font-[700] text-[#1C2347]  mb-4">
                                AI Tokenization – Intelligent Asset Management
                            </h1>
                            <p className="text-lg md:text-[22px] text-[#1C2347] font-[500]">
                                We take tokenization beyond simple digital assets — by infusing it with AI intelligence.
                            </p>
                        </div>

                        {/* === AI Tokenization Solutions === */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-12 mt-16 md:mt-24 text-left">
                            {/* Left Side: Text Content */}
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-[#1C2347]">Our AI Tokenization Solutions Include:</h2>
                                <div className="space-y-5">
                                    {tokenizationSolutions.map(solution => (
                                        <div key={solution.title}>
                                            <h3 className="font-semibold text-[#1C2347]">{solution.title} –</h3>
                                            <p className="text-[#6B6B6B] font-[500]">{solution.description}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="pt-4">
                                    {/* <Button>Book a Demo</Button> */}
                                    <button
                                        className="bg-blue-100 font-[500] px-10 max-md:px-[78px]    py-3 text-white  transition duration-300 ease-in-out  hover:scale-[1.05] hover:bg-gradient-to-l rounded-md mr-4 max-md:mr-0 bg-gradient-to-r from-blue-500 to-purple-600"
                                        onClick={props.demoController}
                                    >
                                        Book a Demo
                                    </button>
                                </div>
                            </div>
                            {/* Right Side: Image */}
                            <div className="flex justify-center items-center">
                                <img
                                    src="/images/blockchain/sectionfourillustration.webp" // <-- Replace with your image
                                    alt="AI Tokenization"
                                    className="max-w-full h-auto"
                                />
                            </div>
                        </div>

                        {/* === Benefits Banner === */}
                        <div
                            className="relative text-white rounded-2xl p-8 md:p-12 my-16 md:my-24 bg-slate-800 bg-cover bg-center"
                            style={{ backgroundImage: "url('/images/blockchain/cubesback.webp')" }} // <-- Replace with your image
                        >
                            {/* <div className="absolute inset-0 bg-slate-900 opacity-60 rounded-2xl"></div> */}
                            <div className="relative z-10 space-y-4">
                                <h3 className="text-2xl font-bold text-left">Benefits:</h3>
                                <ul className="space-y-3">
                                    {benefits.map(benefit => <BenefitItem key={benefit}>{benefit}</BenefitItem>)}
                                </ul>
                            </div>
                        </div>

                    </div>

                    {/* === Why Invincible Ocean? Section === */}
                    <div className="relative overflow-hidden pt-8">
                        {/* Blue wave background element */}
                        <div className="absolute bottom-0 right-0 h-3/4 w-full lg:w-[40%] bg-blue-600 rounded-tl-full" style={{ zIndex: 1 }}></div>

                        <div className="container mx-auto px-6 relative z-10">
                            <div className="grid grid-cols-1 lg:grid-cols-2 items-end gap-12">
                                {/* Left Side: "Why Us" List */}
                                <div className="pb-16 lg:pb-24 text-left">
                                    <h2 className="text-3xl md:text-4xl font-bold text-[#1C2347] mb-8">Why Invincible Ocean?</h2>
                                    <div className="space-y-4 max-w-lg">
                                        {whyUsPoints.map((point, index) => (
                                            <CheckListItem key={point} isHighlighted={index === highlightedIndex}>
                                                {point}
                                            </CheckListItem>
                                        ))}
                                    </div>
                                </div>
                                {/* Right Side: Image of Person */}
                                <div className="relative h-full flex items-end justify-center lg:justify-start">
                                    <img
                                        src="/images/blockchain/indianman.webp" // <-- Replace with your image
                                        alt="Professional working on a laptop"
                                        className="max-w-sm md:max-w-md lg:max-w-none w-full h-auto"
                                        style={{ zIndex: 2 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section ----------------------------------------------------------------------------------V*/}
            <div className="relative bg-gray-900 font-sans overflow-hidden">
                {/* Curved white divider at the top */}
                <div className="absolute top-0 left-0 w-full h-20 bg-gray-50" style={{ clipPath: 'ellipse(100% 50% at 50% 0%)' }}></div>

                {/* Main content container with gradient background */}
                <div className="relative bg-gradient-to-r from-indigo-900 via-blue-900 to-black">

                    {/* Decorative background image (digital chains) */}
                    <div
                        className="absolute inset-0 bg-no-repeat bg-left-top"
                        style={{
                            backgroundImage: "url('/images/blockchain/chainsbackfoot.webp')", // <-- Replace with your image
                            backgroundSize: 'contain',
                        }}
                    ></div>

                    {/* Content wrapper */}
                    <div className="relative container mx-auto px-6 py-24 md:py-32 text-center text-white z-10">
                        <h2 className="text-4xl md:text-5xl font-bold">
                            Let's Build Your Blockchain Future.
                        </h2>
                        <p className="mt-4 max-w-4xl mx-auto text-lg text-gray-300">
                            Whether You're Launching A DeFi Platform, Building An Enterprise Blockchain, Or Creating AI-Powered Tokenized Assets – Invincible Ocean Delivers Products That Last.
                        </p>

                        {/* Action Buttons */}
                        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                className="border-2 bg-[#FFF8F840] text-white max-md:w-full  transition duration-300 ease-in-out hover:scale-[1.05] hover:bg-[#FFF8F860] px-8 max-md: py-3 rounded-md"
                                onClick={props.demoController}
                            >
                                Schedule a Free Consultation
                                <i className="fa fa-long-arrow-right ml-4"></i>
                            </button>
                            <button
                                className="border-2 bg-[#FFF8F840] text-white max-md:w-full  transition duration-300 ease-in-out hover:scale-[1.05] hover:bg-[#FFF8F860] px-8 max-md: py-3 rounded-md"
                                onClick={props.demoController}
                            >
                                Start Your Blockchain Project
                                <i className="fa fa-long-arrow-right ml-4"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
}
